import { extend, jsonClone } from "../utils/vanilla";
import { NODEVICEID, NOUSERID } from "./DutyStatusLog";
import { IHashMap, IModifiedType } from "../sync/sync.types";
import { canSkipCertify } from "../ui/dvir/helper";
import { Device } from "./Device";
import { Trailer } from "./Trailer";
import { IDefect, TDefectAssetType } from "./Defect";
import { DriveStore } from "../store/reducers";
import AppView from "../app.view";

export type TLogType = "PreTrip" | "InTrip" | "PostTrip";
export type TRepairStatus = "NotRepaired" | "Repaired" | "NotNecessary";
export type TResourceType = "trailer" | "device";
export type ILogKey = keyof DVIRLog;

export interface IDefectRemark {
    id?: string;
    remark: string;
    dateTime: string;
    user?: IIdEntity;
    mediaFiles?: IIdEntity[];
}

export interface IDvirDefects {
    id?: string;
    name?: string;
    repairStatus?: TRepairStatus;
    dateTime?: string;
    repairDateTime?: string;
    repairUser?: IIdEntity;
    defectRemarks?: IDefectRemark[];
    dVIRLog?: IIdEntity;
    defect: IDefect | IIdEntity;

    /* eslint-disable @typescript-eslint/naming-convention */
    _dateTime?: string;
    _repairDateTime?: string;
    /* eslint-enable @typescript-eslint/naming-convention */
}

export interface IDVIRLog extends IHashMap<IDVIRLog> {
    dateTime?: string;
    driver?: IUser | "NoUserId";
    device?: Device;
    trailer?: Trailer;

    driverRemark?: string;
    dVIRDefects?: IDvirDefects[];
    logType?: TLogType | "Unknown";
    defectList?: IDefect;

    repairedBy?: IIdEntity | "NoUserId";
    repairDate?: string;
    repairRemark?: string;

    isSafeToOperate?: boolean;
    certifiedBy?: IUser | "NoUserId";
    certifyDate?: string;
    certifyRemark?: string;

    location?: IAddressLookupData;

    hubometer?: number;
    authorityName?: string;
    authorityAddress?: string;
    odometer?: number;
    engineHours?: number;
    loadHeight?: number;
    loadWidth?: number;
    version?: string;
    duration?: string;

    // client side props
    /* eslint-disable @typescript-eslint/naming-convention */
    _odometer?: number;
    _engineHours?: number;
    _tempLocation?: IAddressLookupData;
    _address?: string;
    _country?: string;
    _region?: string;
    _isManualAddress?: boolean;
    _device?: IIdEntity;
    /* eslint-enable @typescript-eslint/naming-convention */
}

export const REPAIR_STATUS_STRINGS = {
    NotNecessary: "Repair not necessary for the safe operation of the vehicle",
    Repaired: "Resolved",
    NotRepaired: "Not repaired"
};

export default class DVIRLog implements IDVIRLog {

    id: string;
    dateTime?: string;
    driver: IUser | "NoUserId";
    device: Device;
    trailer: Trailer;

    driverRemark?: string;
    dVIRDefects?: IDvirDefects[];
    logType: TLogType | "Unknown";
    defectList?: IDefect;

    repairedBy: IIdEntity | "NoUserId";
    repairDate?: string;
    repairRemark?: string;

    isSafeToOperate?: boolean;
    certifiedBy: IUser | "NoUserId";
    certifyDate?: string;
    certifyRemark?: string;

    location?: IAddressLookupData;

    hubometer?: number;
    authorityName?: string;
    authorityAddress?: string;
    odometer?: number;
    engineHours?: number;
    loadHeight?: number;
    loadWidth?: number;
    version?: string;
    duration?: string;

    // client side props
    modified?: IModifiedType;
    previousState?: IDVIRLog;
    /* eslint-disable @typescript-eslint/naming-convention */
    _syncCounter?: number;

    _odometer?: number;
    _engineHours?: number;
    _tempLocation?: IAddressLookupData;
    _address?: string;
    _country?: string;
    _region?: string;
    _isManualAddress?: boolean;
    _device?: IIdEntity;

    constructor (obj?: IDVIRLog) {
        this.id = "";
        this.device = new Device({ id: NODEVICEID });
        this.trailer = new Trailer({ id: "NoTrailerId" });
        this.logType = "Unknown";
        this.driver = NOUSERID;
        this.dVIRDefects = [];
        this.repairedBy = NOUSERID;
        this.certifiedBy = NOUSERID;

        this._syncCounter = 5;

        this._isManualAddress = false;

        if (obj && Object.keys(obj).length > 0) {
            this.id = obj.id || "";
            this.dateTime = obj.dateTime;
            this.driver = obj.driver || NOUSERID;
            this.device = obj.device || new Device({ id: NODEVICEID });
            this.trailer = obj.trailer || new Trailer({ id: "NoTrailerId" });

            this.driverRemark = obj.driverRemark;
            this.dVIRDefects = obj.dVIRDefects || [];
            this.logType = obj.logType || "Unknown";
            this.defectList = obj.defectList;

            this.repairedBy = obj.repairedBy || NOUSERID;
            this.repairDate = obj.repairDate;
            this.repairRemark = obj.repairRemark;

            this.isSafeToOperate = obj.isSafeToOperate;

            this.certifiedBy = obj.certifiedBy || NOUSERID;
            this.certifyDate = obj.certifyDate;
            this.certifyRemark = obj.certifyRemark;

            this.location = obj.location || undefined;
            this.hubometer = obj.hubometer;

            this.authorityName = obj.authorityName;
            this.authorityAddress = obj.authorityAddress;
            this.odometer = obj.odometer;
            this.engineHours = obj.engineHours;

            this.loadWidth = obj.loadWidth;
            this.loadHeight = obj.loadHeight;
            this.version = obj.version;
            this.duration = obj.duration;

            this.modified = obj.modified;
            this.previousState = obj.previousState;
            this._syncCounter = obj._syncCounter || 5;

            this._odometer = obj._odometer;
            this._engineHours = obj._engineHours;
            this._tempLocation = obj._tempLocation;
            this._address = obj._address;
            this._country = obj._country;
            this._region = obj._region;
            this._isManualAddress = obj._isManualAddress || false;
            this._device = obj._device;
        }
    }
    /* eslint-enable @typescript-eslint/naming-convention */

    getAssetType(): TDefectAssetType {
        return this.trailer.id === "NoTrailerId" ? "Device" : "Trailer";
    }

    getLocation(): IAddressLookupData | undefined {
        let location = this.location || this._tempLocation || undefined;

        // both 0 means we didn't find anything
        if (location && location.location && !(location.location.x === 0 && location.location.y === 0)) {
            return location;
        }
        return undefined;
    }

    isActive(): boolean {
        return (this.getResource().id !== NODEVICEID && this.getResource().id !== "NoTrailerId");
    }

    getResource(): Device | Trailer {
        return this[this.getResourceType()];
    }

    getResourceType(): TResourceType {
        return this.trailer.id === "NoTrailerId" ? "device" : "trailer";
    }

    getRepairedDefects(): IDvirDefects[] {
        return this.dVIRDefects ? this.dVIRDefects.filter(dvirDefect => dvirDefect.repairStatus !== "NotRepaired" && dvirDefect.repairStatus !== "NotNecessary") : [];
    }

    getUnrepairedDefects(includeNotNecessary?: boolean): IDvirDefects[] {
        return this.dVIRDefects ? this.dVIRDefects.filter(dvirDefect => dvirDefect.repairStatus === "NotRepaired" || (includeNotNecessary && dvirDefect.repairStatus === "NotNecessary")) : [];
    }

    getCarryOverDefects(): IDvirDefects[] {
        return this.dVIRDefects ? this.dVIRDefects.filter(dvirDefect => dvirDefect.repairStatus === "NotNecessary" || dvirDefect.repairStatus === "NotRepaired") : [];
    }

    hasDefects(): boolean {
        return !!(this.dVIRDefects && this.dVIRDefects.length > 0);
    }

    hasBlockingUnrepairedDefect(store: DriveStore): boolean {
        let defects = this.dVIRDefects;
        if (!defects || defects.length === 0) {
            return false;
        }
        if (canSkipCertify(store, this._device)) {
            return defects.some(defect => (defect.defect as IDefect).severity === "Critical" && defect.repairStatus === "NotRepaired");
        }
        return defects.some(defect => (defect.defect as IDefect).severity !== "Unregulated" && defect.repairStatus === "NotRepaired");
    }

    hasRepairedDefects (): boolean {
        return !!this.dVIRDefects?.some(defect => defect.repairStatus !== "NotRepaired");
    }

    hasUnrepairedDefects(includeNotNecessary?: boolean): boolean {
        return !!this.dVIRDefects?.some(defect => defect.repairStatus === "NotRepaired" || (includeNotNecessary && defect.repairStatus === "NotNecessary"));
    }

    hasCriticalUnrepairedDefects(): boolean {
        return !!this.dVIRDefects?.some(defect => (defect.defect as IDefect).severity === "Critical" && defect.repairStatus === "NotRepaired");
    }

    isCritical(): boolean {
        return !!this.dVIRDefects?.some((defect: any) => defect.defect.severity && defect.defect.severity === "Critical");
    }

    getDefectToRepair(defectId: string) {
        let defects = this.dVIRDefects;
        if (defectId && defects) {
            for (let i = 0; i < defects.length; i++) {
                if (defects[i].id === defectId) {
                    return defects[i];
                }
            }
        }
        return null;
    }

    isInTrip(): boolean {
        return this.logType === "InTrip";
    }

    isRepaired (): boolean {
        return !this.hasUnrepairedDefects();
    }

    isSafe (): boolean {
        return this.isSafeToOperate === true;
    }

    isCertified (): boolean {
        return this.certifyDate !== undefined;
    }

    isComplete(app: AppView): boolean {
        if (this.hasDefects()) {
            if (!canSkipCertify(app.store, this._device)) {
                return this.isCertified();
            }
            return !this.hasBlockingUnrepairedDefect(app.store);
        }
        return true;
    }

    isDvirLogAfterDate (date: string): boolean {
        return !!this.dateTime && this.dateTime > date;
    }

    needsReview (store: DriveStore): boolean {
        return this.hasDefects() && !canSkipCertify(store, this._device) && !this.isCertified() && !this.hasBlockingUnrepairedDefect(store);
    }

    needsRepair (): boolean {
        return !this.isCertified() && this.hasDefects() && !this.isRepaired();
    }

    // If there is a defect without severity populated, it is probably because we don't have the defect list
    hasUnknownUnrepairedDefect (): boolean {
        return !!this.dVIRDefects?.some(defect => !(defect.defect as IDefect).severity && defect.repairStatus === "NotRepaired");
    }

    // We modify this for display, need to clone it as not to affect the original model
    getClonedAllDefects (): IDvirDefects[] {
        return jsonClone(this.dVIRDefects);
    }

    getClonedUnrepairedDefects (): IDvirDefects[] {
        return jsonClone(this.getUnrepairedDefects(true));
    }

    getClonedRepairedDefects (): IDvirDefects[] {
        return jsonClone(this.getRepairedDefects());
    }

    toJSON (cleanVersion = false) {
        let clone: any = extend({}, this);
        let defectsWithIds: any[] = [];

        // Storing device in the local storage as { id: <device.id || NODEVICEID> }
        if (clone.device) {
            clone.device = { "id": clone.device.id };
        }

        if (clone.trailer) {
            clone.trailer = { "id": clone.trailer.id };
        }

        if (clone.driver && clone.driver !== NOUSERID) {
            clone.driver = { "id": clone.driver.id };
        }

        if (clone.repairedBy && clone.repairedBy !== NOUSERID) {
            clone.repairedBy = { "id": clone.repairedBy.id };
        }

        if (clone.certifiedBy && clone.certifiedBy !== NOUSERID) {
            clone.certifiedBy = { "id": clone.certifiedBy.id };
        }

        clone.dVIRDefects.forEach(function (item: {id?: string}) {
            if (!item.hasOwnProperty("id")) {
                defectsWithIds.push({"id": item});
            } else {
                defectsWithIds.push(item);
            }
        });

        clone.dVIRDefects = defectsWithIds;

        if (cleanVersion || false) {
            delete clone.version;
        }

        return clone;
    }

    /* Sometimes Drive attempts to create log with NoDeviceId and NoTrailerId. Added error reporting for such cases. See case 227754 */
    validateResource() {
        if (this.device.id === NODEVICEID && this.trailer.id === "NoTrailerId") {
            throw new Error("Invalid DVIR arguments. It must include either device or trailer.");
        }
    }
}